/**
 * Store-currency formatting for the Shopify coupon surfaces.
 *
 * Shopify denominates every fixed-amount discount in the STORE'S BASE
 * currency — `DiscountAmountInput.amount` carries no currency field, and
 * Shopify's own docs are explicit that "fixed-amount discounts can be created
 * only in your store's default currency", converting to the buyer's
 * presentment currency at checkout. So there is exactly one currency per
 * store, and every amount the brand types (discount amount, minimum purchase
 * subtotal) is in it. Multi-currency support here therefore means "label the
 * money correctly per store", not "hold several amounts".
 */

import { DISPLAY_DECIMALS } from '../../lib/format';

/** Currency of last resort. The store's currency is captured on its
 *  installation record at install time and refreshed by every successful
 *  store read, and the BE serves that stored value first — so this is reached
 *  only for a coupon with no snapshot whose store's install-time capture ALSO
 *  failed and has not been refreshed yet. Shown until the real value arrives.
 *
 *  USD by product decision: the platform is not INR-only, and a neutral
 *  default is preferable to asserting the home market. */
export const FALLBACK_CURRENCY_CODE = 'USD';

/**
 * Resolves which currency to display, newest-known-good first:
 *   1. the coupon's own snapshot — what it was actually minted in, which
 *      stays right even after the brand connects a different store;
 *   2. the currently connected store's live currency, for anything not yet
 *      minted (the config form, an un-snapshotted legacy coupon);
 *   3. FALLBACK_CURRENCY_CODE.
 * Each argument is optional so callers pass only what they have.
 */
export function resolveCurrencyCode(snapshot?: string, storeCurrency?: string): string {
  return snapshot || storeCurrency || FALLBACK_CURRENCY_CODE;
}

/** `Intl` throws on a malformed currency code rather than degrading, and the
 *  code here comes off the wire from Shopify — so a bad value must not take
 *  the whole coupon table down with it. Formatters are also not free to
 *  construct, and these render in every table row, so they're memoised per
 *  code. */
const formatterCache = new Map<string, Intl.NumberFormat | null>();

function currencyFormatter(code: string): Intl.NumberFormat | null {
  const cached = formatterCache.get(code);
  if (cached !== undefined) return cached;
  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: code,
    // Platform standard: money always shows exactly two decimals, so a
    // displayed amount can be diffed against Shopify's own order total.
    minimumFractionDigits: DISPLAY_DECIMALS,
    maximumFractionDigits: DISPLAY_DECIMALS,
  };
  let formatter: Intl.NumberFormat | null;
  try {
    // narrowSymbol keeps the label the merchant's own — a USD store reads "$20",
    // not the locale-disambiguated "US$20" a non-US browser locale would
    // otherwise produce for its own reader.
    formatter = new Intl.NumberFormat(undefined, { ...options, currencyDisplay: 'narrowSymbol' });
  } catch {
    try {
      formatter = new Intl.NumberFormat(undefined, options);
    } catch {
      formatter = null;
    }
  }
  formatterCache.set(code, formatter);
  return formatter;
}

/** A money amount in the store's currency — "₹500", "$20". Falls back to
 *  "<CODE> <amount>" for a code `Intl` doesn't recognise, so an unexpected
 *  currency is never silently mislabelled as rupees. */
export function formatStoreMoney(amount: number, currencyCode: string): string {
  if (!Number.isFinite(amount)) return '—';
  const formatter = currencyFormatter(currencyCode);
  return formatter ? formatter.format(amount) : `${currencyCode} ${amount.toFixed(DISPLAY_DECIMALS)}`;
}

/** Just the symbol ("₹", "$", "€"), for input adornments and field labels
 *  where the amount itself lives in the input. Derived from the same
 *  formatter so the symbol and the formatted value can never disagree; falls
 *  back to the ISO code, which is always a meaningful label. */
export function currencySymbol(currencyCode: string): string {
  const formatter = currencyFormatter(currencyCode);
  if (!formatter) return currencyCode;
  const part = formatter.formatToParts(0).find((p) => p.type === 'currency');
  return part?.value ?? currencyCode;
}
